import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  ConfirmationNumber as TicketIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../contexts/ThemeContext';
import apiService from '../../services/apiService';

interface TicketResponse {
  id: string;
  message: string;
  createdAt: string;
  admin: {
    firstName: string;
    lastName: string;
    preferredName?: string;
  };
}

interface Ticket {
  id: string;
  subject: string;
  message: string;
  priority: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  responses?: TicketResponse[];
}

const MyTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const { actualMode: mode } = useThemeContext();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getUserSupportTickets();
      setTickets(response.data.tickets || []);
    } catch (err: any) {
      console.error('Failed to fetch tickets:', err);
      setError(err.response?.data?.message || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
      case 'high':
        return '#D32F2F';
      case 'medium':
        return '#ED6C02';
      case 'low':
        return '#2E7D32';
      default:
        return '#757575';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'Open';
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  };

  const handleTicketClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedTicket(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={() => navigate('/support')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #4CAF50, #2E7D32)'
                : 'linear-gradient(135deg, #66BB6A, #4CAF50)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            🎫 My Support Tickets
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {tickets.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }} elevation={2}>
            <TicketIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Support Tickets Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You haven't submitted any support tickets. Need help? Create a new ticket to get assistance.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/support')}
              sx={{
                background: 'linear-gradient(135deg, #66BB6A, #4CAF50)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4CAF50, #2E7D32)',
                }
              }}
            >
              Create Support Ticket
            </Button>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {tickets.map((ticket) => (
              <Card
                key={ticket.id}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleTicketClick(ticket)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {ticket.subject}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mb: 2
                        }}
                      >
                        {ticket.message}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                      <Chip
                        label={getStatusLabel(ticket.status)}
                        color={getStatusColor(ticket.status)}
                        size="small"
                      />
                      <Chip
                        label={ticket.priority}
                        size="small"
                        sx={{
                          backgroundColor: getPriorityColor(ticket.priority) + '20',
                          color: getPriorityColor(ticket.priority),
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatDate(ticket.createdAt)}
                      </Typography>
                    </Box>
                    {ticket.resolvedAt && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Resolved: {formatDate(ticket.resolvedAt)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TicketIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        ID: {ticket.id.substring(0, 8).toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Ticket Detail Dialog */}
        <Dialog
          open={showDetail}
          onClose={handleCloseDetail}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Ticket Details
            </Typography>
            <IconButton onClick={handleCloseDetail}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            {selectedTicket && (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <Chip
                    label={getStatusLabel(selectedTicket.status)}
                    color={getStatusColor(selectedTicket.status)}
                  />
                  <Chip
                    label={selectedTicket.priority}
                    sx={{
                      backgroundColor: getPriorityColor(selectedTicket.priority) + '20',
                      color: getPriorityColor(selectedTicket.priority),
                      fontWeight: 'bold'
                    }}
                  />
                </Box>

                <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Subject
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {selectedTicket.subject}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Message
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.message}
                  </Typography>
                </Paper>

                {/* Conversation Thread */}
                {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Conversation
                      </Typography>
                    </Divider>

                    <Stack spacing={2} sx={{ mb: 3 }}>
                      {selectedTicket.responses.map((response) => (
                        <Paper
                          key={response.id}
                          sx={{
                            p: 2,
                            backgroundColor: '#E3F2FD',
                            borderLeft: '4px solid #2196F3'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon sx={{ fontSize: 20, color: '#2196F3' }} />
                              <Typography variant="subtitle2" fontWeight="bold" color="#2196F3">
                                {response.admin.preferredName || response.admin.firstName} {response.admin.lastName ? response.admin.lastName.charAt(0) + '.' : ''} (Support Team)
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(response.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                            {response.message}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </>
                )}

                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ticket Information
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      <strong>Ticket ID:</strong> {selectedTicket.id.substring(0, 8).toUpperCase()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong> {formatDate(selectedTicket.createdAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Last Updated:</strong> {formatDate(selectedTicket.updatedAt)}
                    </Typography>
                    {selectedTicket.resolvedAt && (
                      <Typography variant="body2">
                        <strong>Resolved:</strong> {formatDate(selectedTicket.resolvedAt)}
                      </Typography>
                    )}
                    {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                      <Typography variant="body2">
                        <strong>Responses:</strong> {selectedTicket.responses.length} {selectedTicket.responses.length === 1 ? 'reply' : 'replies'}
                      </Typography>
                    )}
                  </Stack>
                </Paper>

                {selectedTicket.status.toLowerCase() === 'in_progress' && (
                  <Alert severity="info" icon={<MessageIcon />}>
                    <Typography variant="body2">
                      Your ticket is being reviewed by our support team. You'll receive an email when there's an update.
                    </Typography>
                  </Alert>
                )}

                {selectedTicket.status.toLowerCase() === 'resolved' && (
                  <Alert severity="success">
                    <Typography variant="body2">
                      This ticket has been resolved. If you need further assistance, please create a new support ticket.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDetail}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default MyTicketsPage;
